import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CREATE_TASK,
  CREATE_TASKS_BY_OUTCOME,
  CREATE_TASKS_BY_SIGNAL,
  GET_TASK,
  GET_TASKS,
  PROJECT_API_KEY,
  TASK_ACTION,
  UPDATE_TASK,
} from 'src/@core/services/util/api.endpoints';

export type TaskActionType = 'approve' | 'reject' | 'pause' | 'cancel';

export interface TaskActionRequest {
  task_id: string;
  action: TaskActionType;
  reason?: string;
  when?: string;
  scheduled_at?: string;
  /** Optional jobs_status keys — help cancel open_tasks-only rows. */
  job_id?: string;
  entity_id?: string;
}

export type TaskStatus =
  | 'awaiting_approval'
  | 'scheduled'
  | 'running'
  | 'completed'
  | 'paused'
  | 'failed'
  | 'cancelled';

export type TaskOwnerType = 'ai' | 'human' | 'unassigned';
export type TaskChannel = 'call' | 'whatsapp' | 'email' | 'human';
export type TaskTab =
  | 'awaiting_approval'
  | 'scheduled_today'
  | 'overdue'
  | 'ai_tasks'
  | 'human_tasks';

export interface TaskActions {
  can_approve?: boolean;
  can_reject?: boolean;
  can_cancel?: boolean;
  can_edit?: boolean;
  can_pause?: boolean;
  can_view?: boolean;
  can_complete?: boolean;
}

export interface TaskListItem {
  task_id: string;
  lead_name?: string;
  lead_number?: string;
  people_id?: string;
  project_id?: string;
  project_name?: string;
  outcome?: string;
  title?: string;
  channel?: string;
  task_type?: string;
  scheduled_at?: string;
  owner_type?: TaskOwnerType | string;
  owner_id?: string;
  owner_name?: string;
  status?: TaskStatus | string;
  instructions?: string;
  template_id?: string;
  template_name?: string;
  job_id?: string;
  actions?: TaskActions;
  executed?: boolean;
  executed_at?: string;
  approved?: boolean;
  approval_status?: string;
  completion_reason?: string;
  source?: string;
  created_at?: number;
  updated_at?: number;
}

export interface TaskSummary {
  total: number;
  awaiting_approval: number;
  scheduled: number;
  running: number;
  completed: number;
  paused: number;
  failed: number;
  cancelled: number;
  /** False when the counts are estimated rather than counted. */
  is_exact?: boolean;
  /** Epoch seconds the summary was computed at. */
  computed_at?: number;
  /** Status buckets added by the backend after this interface was written. */
  [key: string]: number | boolean | undefined;
}

export interface TasksListResponse {
  data: TaskListItem[];
  summary: TaskSummary;
  /** Snake_case is what the API actually returns; camelCase kept for legacy callers. */
  pagination?: {
    current_page?: number;
    page_size?: number;
    total_records?: number;
    total_pages?: number;
    has_next_page?: boolean;
    has_previous_page?: boolean;
    is_approximate?: boolean;
    totalElements?: number;
    pageNum?: number;
    pageSize?: number;
    totalPages?: number;
  };
}

export interface TaskDetailResponse extends TaskListItem {
  lead?: any;
  timeline?: any[];
  ai_memory?: string | string[] | Record<string, any>;
  actions?: TaskActions;
  approval_status?: string;
  source?: string;
}

export interface GetTaskApiResponse {
  success?: boolean;
  task?: TaskDetailResponse;
}

export interface TasksQuery {
  entity_id: string;
  project_id?: string;
  status?: TaskStatus | string;
  owner_type?: TaskOwnerType | string;
  owner_id?: string;
  channel?: TaskChannel | string;
  task_type?: string;
  outcome?: string;
  search?: string;
  tab?: TaskTab | string;
  pageNum?: number;
  pageSize?: number;
}

export interface CreateTaskPayload {
  entity_id: string;
  project_id: string;
  people_id: string;
  title: string;
  scheduled_at?: string;
  when?: string;
  channel?: TaskChannel | string;
  task_type?: string;
  owner_type?: TaskOwnerType | string;
  owner_id?: string;
  owner_name?: string;
  instructions?: string;
  task_prompt?: string;
  template_id?: string;
  template_name?: string;
  requires_approval?: boolean;
  lead_name?: string;
  lead_number?: string;
}

export interface CreateTasksByOutcomePayload {
  entity_id: string;
  project_id: string;
  outcome: string;
  title: string;
  channel?: TaskChannel | string;
  task_type?: string;
  scheduled_at?: string;
  when?: string;
  owner_type?: TaskOwnerType | string;
  owner_id?: string;
  owner_name?: string;
  task_prompt?: string;
  disable_existing_tasks?: boolean;
}

/** Bulk create by latest jobs_status.signal.strength — POST /createTasksBySignal. */
export interface CreateTasksBySignalPayload {
  entity_id: string;
  project_id: string;
  strengths: string[];
  title: string;
  channel?: TaskChannel | string;
  task_type?: string;
  scheduled_at?: string;
  when?: string;
  owner_type?: TaskOwnerType | string;
  owner_id?: string;
  owner_name?: string;
  /** Shared override; empty/omitted → each lead's leads.task_prompt. */
  task_prompt?: string;
  disable_existing_tasks?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TasksService {

  constructor(private http: HttpClient) { }

  private headers(): HttpHeaders {
    return new HttpHeaders({
      'x-api-key': PROJECT_API_KEY,
      'Content-Type': 'application/json'
    });
  }

  getTasks(query: TasksQuery): Observable<TasksListResponse> {
    let params = new HttpParams().set('entity_id', query.entity_id);

    const optionalKeys: Array<keyof TasksQuery> = [
      'project_id', 'status', 'owner_type', 'owner_id', 'channel',
      'task_type', 'outcome', 'search', 'tab'
    ];
    optionalKeys.forEach((key) => {
      const value = query[key];
      if (value != null && String(value).trim() !== '') {
        params = params.set(key, String(value));
      }
    });

    if (query.pageNum != null) {
      params = params.set('pageNum', String(query.pageNum));
    }
    if (query.pageSize != null) {
      params = params.set('pageSize', String(query.pageSize));
    }

    return this.http.get<TasksListResponse>(GET_TASKS, { headers: this.headers(), params });
  }

  getTask(taskId: string): Observable<TaskDetailResponse> {
    const params = new HttpParams().set('task_id', taskId);
    return this.http.get<GetTaskApiResponse | TaskDetailResponse>(GET_TASK, { headers: this.headers(), params }).pipe(
      map((res) => {
        if (res && (res as GetTaskApiResponse).task) {
          return (res as GetTaskApiResponse).task as TaskDetailResponse;
        }
        return res as TaskDetailResponse;
      })
    );
  }

  createTask(payload: CreateTaskPayload): Observable<any> {
    return this.http.post(CREATE_TASK, payload, { headers: this.headers() });
  }

  createTasksByOutcome(payload: CreateTasksByOutcomePayload): Observable<any> {
    return this.http.post(CREATE_TASKS_BY_OUTCOME, payload, { headers: this.headers() });
  }

  createTasksBySignal(payload: CreateTasksBySignalPayload): Observable<any> {
    return this.http.post(CREATE_TASKS_BY_SIGNAL, payload, { headers: this.headers() });
  }

  updateTask(payload: Record<string, any>): Observable<any> {
    return this.http.patch(UPDATE_TASK, payload, { headers: this.headers() });
  }

  taskAction(payload: TaskActionRequest): Observable<any> {
    const body: Record<string, string> = {
      task_id: payload.task_id,
      action: payload.action,
    };
    if (payload.reason && payload.reason.trim()) {
      body.reason = payload.reason.trim();
    }
    if (payload.when && payload.when.trim()) {
      body.when = payload.when.trim();
    }
    if (payload.scheduled_at && payload.scheduled_at.trim()) {
      body.scheduled_at = payload.scheduled_at.trim();
    }
    if (payload.job_id && payload.job_id.trim()) {
      body.job_id = payload.job_id.trim();
    }
    if (payload.entity_id && payload.entity_id.trim()) {
      body.entity_id = payload.entity_id.trim();
    }
    return this.http.post(TASK_ACTION, body, { headers: this.headers() });
  }

  approveTask(
    taskId: string,
    options?: { when?: string; scheduled_at?: string }
  ): Observable<any> {
    const payload: TaskActionRequest = {
      task_id: taskId,
      action: 'approve',
    };
    if (options && options.when) {
      payload.when = options.when;
    }
    if (options && options.scheduled_at) {
      payload.scheduled_at = options.scheduled_at;
    }
    return this.taskAction(payload);
  }

  rejectTask(taskId: string, reason?: string): Observable<any> {
    return this.taskAction({ task_id: taskId, action: 'reject', reason });
  }

  pauseTask(taskId: string, reason?: string): Observable<any> {
    return this.taskAction({ task_id: taskId, action: 'pause', reason });
  }

  /**
   * Soft-delete / cancel an open task (Connected Edit tasks delete).
   * Prefer passing job_id + entity_id so jobs_status-only rows still cancel.
   */
  cancelTask(
    taskId: string,
    options?: { reason?: string; job_id?: string; entity_id?: string }
  ): Observable<any> {
    const payload: TaskActionRequest = {
      task_id: taskId,
      action: 'cancel',
    };
    if (options) {
      if (options.reason) {
        payload.reason = options.reason;
      }
      if (options.job_id) {
        payload.job_id = options.job_id;
      }
      if (options.entity_id) {
        payload.entity_id = options.entity_id;
      }
    }
    return this.taskAction(payload);
  }
}
